import classNames from "classnames"
import { ComponentProps, ReactElement, ReactNode, forwardRef, useMemo } from "react"
import { TableRowProps } from "./types"
import { TableHeader } from "./TableHeader"
import { useTableContext } from "./TableContext"
import { Checkbox } from "../Checkbox"
import { FormLabel } from "../FormLabel"
import { TableCell, Table } from "./Table"
import { devWarning } from "../utils/devWarning"
import { noop } from "lodash-es"
import { useId } from "@floating-ui/react"

function ColumnHeaderCheckbox() {
  const { onChangeMultiSelectionState } = useTableContext()

  devWarning(
    !onChangeMultiSelectionState,
    "Table is missing onChangeMultiSelectionState prop. Please add it to ensure that the Table SelectAll Menu works properly",
  )
  return <Table.RowSelectMenu onSelectOption={onChangeMultiSelectionState || noop} />
}

export const SelectableTableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  (
    {
      children = null,
      customCheckbox: customCheckboxProp,
      isSelected,
      onSelectRow,
      rowSelectToggleLabel = "Toggle row selected",
      ...rest
    },
    ref,
  ) => {
    const id = useId()
    const firstChild = (Array.isArray(children) ? children[0] : children) as ReactElement<
      ComponentProps<typeof TableHeader> | ComponentProps<typeof TableCell>
    >
    const isColumnHeader = firstChild.props?.scope === "col"
    const customCheckbox: ReactNode = useMemo(
      () =>
        customCheckboxProp?.({
          label: rowSelectToggleLabel,
          isSelected: isSelected ?? false,
          onSelectRow,
        }),
      [customCheckboxProp, isSelected, onSelectRow, rowSelectToggleLabel],
    )

    devWarning(
      isColumnHeader && typeof isSelected !== "undefined",
      "You are using isSelected on a <Table.Row/> with column headers. This prop will have no effect on this component",
    )

    const checkboxCell = isColumnHeader ? (
      <Table.Header scope="col" className="w-[68px]">
        {customCheckbox || <ColumnHeaderCheckbox />}
      </Table.Header>
    ) : (
      // onClick is here to ensure that the checkbox works when the rows of the table are clickable
      <Table.Cell onClick={event => event.stopPropagation()} className="w-[68px]">
        {customCheckbox || (
          <Checkbox
            label={<FormLabel className="sr-only" label={rowSelectToggleLabel} />}
            checked={isSelected}
            onChange={onSelectRow}
            id={id} // TODO: remove when the <label for/> in Checkbox has been removed. We currently need this so that the different checkboxes have their own labels, instead of sharing from the same one as they all have the same id of `"checkbox-undefined"`s
          />
        )}
      </Table.Cell>
    )

    return (
      <tr aria-selected={isSelected} ref={ref} {...rest}>
        {checkboxCell}
        {children}
      </tr>
    )
  },
)

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, isSelected, customCheckbox, onSelectRow, ...props }, ref) => {
    const { isSelectable } = useTableContext()
    const _className = classNames(
      "aria-selected:bg-primary-lighten-100 shadow-gray-200 bg-white", // Apply aria-selected class here in case user manually adds the aria-selected attribute
      className,
    )

    if (isSelectable) {
      return (
        <SelectableTableRow
          className={_className}
          isSelected={isSelected}
          onSelectRow={onSelectRow}
          customCheckbox={customCheckbox}
          {...props}
          ref={ref}
        />
      )
    }

    return <tr className={_className} ref={ref} {...props} />
  },
)

TableRow.displayName = "Table.Row"
